import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Draws `children` at a fixed size and scales them down to fit the box this
 * fills - never up. For an illustration with a fixed footprint (the dashboard's
 * flashcard stack is pixel-positioned by GSAP) that has to live in a box whose
 * height the layout decides: without it, a short window drew the stack over
 * the card's title.
 *
 * Fills its parent (`h-full w-full`), so the parent must have a height.
 */
export function FitBox({
  width,
  height,
  align = 'center',
  children,
}: {
  /** The illustration's own footprint, everything it draws included. */
  width: number;
  height: number;
  align?: 'center' | 'start';
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => {
      const { width: w, height: h } = el.getBoundingClientRect();
      if (w <= 0 || h <= 0) return;
      setScale(Math.min(1, w / width, h / height));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width, height]);

  return (
    <div
      ref={box}
      className={`flex h-full w-full items-center overflow-hidden ${
        align === 'center' ? 'justify-center' : 'justify-start'
      }`}
    >
      <div
        className='relative shrink-0'
        style={{ width: width * scale, height: height * scale }}
      >
        <div
          className='absolute left-0 top-0 origin-top-left'
          style={{ width, height, transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
